import type { WhatsAppTemplate } from '@/services/whatsappService'
import type { CampaignStep, VariableMappingEntry } from '@/services/crmCampaignsService'

/** Las claves de la fuente `crm_demand` que resuelve el servidor. */
export const DEMAND_VARIABLES = ['nombre', 'municipio', 'gestor_nombre', 'precio_hasta', 'tipo_inmueble'] as const

/** La cadencia del caso de Pablo (R3.6), como punto de partida. */
export const DEFAULT_CADENCE = [0, 3, 7, 14, 21]
export const MAX_STEPS = 10

export function templateKey(name: string, language: string): string {
  return `${name}:${language}`
}

export function templateBodyText(template: WhatsAppTemplate | undefined): string {
  return template?.components?.find((component) => component.type?.toUpperCase() === 'BODY')?.text ?? ''
}

/** Las posiciones `{{n}}` del cuerpo, sin repetir y en orden. */
export function templateVariablePositions(template: WhatsAppTemplate | undefined): number[] {
  const matches = templateBodyText(template).match(/\{\{(\d+)\}\}/g) ?? []
  const positions = new Set(matches.map((match) => Number(match.replace(/\D/g, ''))))
  return [...positions].sort((a, b) => a - b)
}

function templateButtons(template: WhatsAppTemplate) {
  return template.components?.flatMap((component) => component.buttons ?? []) ?? []
}

export function hasDynamicUrlButton(template: WhatsAppTemplate | undefined): boolean {
  if (!template) return false
  return templateButtons(template).some(
    (button) => button.type?.toUpperCase() === 'URL' && /\{\{\d+\}\}/.test(button.url ?? ''),
  )
}

/** El servicio no filtra por `APPROVED` (design §5.2): lo hace quien muestra la lista. */
export function approvedTemplates(templates: WhatsAppTemplate[] | undefined): WhatsAppTemplate[] {
  return (templates ?? []).filter((template) => template.status?.toUpperCase() === 'APPROVED')
}

/** Lo que puede ir en un paso de campaña: aprobada y sin botón FLOW. */
export function campaignStepTemplates(templates: WhatsAppTemplate[] | undefined): WhatsAppTemplate[] {
  return approvedTemplates(templates).filter(
    (template) => !templateButtons(template).some((button) => button.type?.toUpperCase() === 'FLOW'),
  )
}

/** Conserva lo ya escrito en cada posición y deja hueco para las nuevas. */
export function mappingForPositions(
  positions: number[],
  previous: VariableMappingEntry[],
): VariableMappingEntry[] {
  return positions.map(
    (position) =>
      previous.find((entry) => entry.position === position) ?? {
        position,
        source_type: 'crm_demand',
        source_key: '',
      },
  )
}

/** Un ejemplo de mensaje: el cuerpo con las variables de la simulación puestas. */
export function renderTemplateBody(body: string, variables: Record<string, string>): string {
  return body.replace(/\{\{(\d+)\}\}/g, (slot, position: string) => variables[position] ?? slot)
}

export function emptyStep(order: number, dayOffset: number, sendTime: string): CampaignStep {
  return {
    orden: order,
    plantilla_nombre: '',
    plantilla_idioma: 'es_ES',
    mapeo_variables: [],
    dia_offset: dayOffset,
    hora_envio: sendTime,
    enlace_buscador: false,
    enlace_fijo: '',
  }
}
